import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CreatePostModal from '../components/CreatePostModal';
import BottomNavigation from '../components/BottomNavigation';
import DrawerMenu from '../components/DrawerMenu';
import MediaImage from '../components/MediaImage';
import Avatar from '../components/Avatar';
import ImageDebugger from '../components/ImageDebugger';
import SearchBar from '../components/SearchBar';
import PostInteractions from '../components/PostInteractions';
import postService, { Post } from '../services/postService';
import socketService from '../services/socketService';
import { useAuth } from '../context/AuthContext';
import MyProfile from './MyProfile';
import NotificationsPage from './NotificationsPage';
import MessagesPage from './MessagesPage';
import CollectionsPage from './CollectionsPage';
import SubscriptionsPage from './SubscriptionsPage';
import Settings from './Settings';
import Analytics from './Analytic';
import CreatorApplication from './CreatorApplication';
import AdminDashboard from './AdminDashboard';
import KYCAdminPanel from './KYCAdminPanel';
import { useWallet } from '@solana/wallet-adapter-react';


type ViewType = 'home' | 'profile' | 'notifications' | 'messages' | 'collections' | 'subscriptions' | 'settings' | 'analytics' | 'creator-application' | 'admin-dashboard' | 'kyc-admin';

const Home: React.FC = () => {
    const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeView, setActiveView] = useState<ViewType>('home');
    const { user, token, logout } = useAuth();
    const navigate = useNavigate();
    const previousView = useRef<ViewType>('home');
    const currentView = useRef<ViewType>('home');
    const socketConnected = useRef(false);

    const { disconnect, connected } = useWallet();

    const handleLogout = async () => {
        await disconnect();
        await logout();
    };

    const handleOpenCreatePost = () => {
        setIsCreatePostModalOpen(true);
    };

    const handleCloseCreatePost = () => {
        setIsCreatePostModalOpen(false);
    };

    const handlePostCreated = () => {
        // Refresh posts when a new post is created
        fetchPosts();
    };

    const handleViewChange = (view: ViewType) => {
        previousView.current = activeView;
        currentView.current = view;
        setActiveView(view);

        // If returning to home from another view, refresh posts
        if (view === 'home' && previousView.current !== 'home') {
            console.log('Returning to home, refreshing posts...');
            fetchPosts();
        }
    };

    const fetchPosts = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('Fetching posts from backend...');
            const response = await postService.getPosts({ page: 1, limit: 20 });
            console.log('Posts received:', response.posts);

            // Debug log media URLs
            response.posts.forEach((post, index) => {
                console.log(`Post ${index + 1} (${post.id}):`, {
                    title: post.title,
                    mediaUrls: post.mediaUrls,
                    userAvatar: post.user.avatar,
                    mediaTypes: post.mediaTypes
                });
            });

            setPosts(response.posts);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch posts');
            console.error('Error fetching posts:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();

        // Listen for create post event from bottom navigation
        const handleOpenCreatePost = () => {
            setIsCreatePostModalOpen(true);
        };

        window.addEventListener('openCreatePost', handleOpenCreatePost);

        // Connect to WebSocket for real-time updates
        if (user?.id && !socketConnected.current) {
            console.log('Connecting to WebSocket for real-time updates...');
            socketService.connect(user.id);
            socketConnected.current = true;

            // Handle new public posts
            const handleNewPublicPost = (data: any) => {
                console.log('Received new public post:', data);
                if (data.post && currentView.current === 'home') {
                    setPosts(prevPosts => {
                        // Check if post already exists to avoid duplicates
                        const exists = prevPosts.some(p => p.id === data.post.id);
                        if (exists) return prevPosts;
                        return [data.post, ...prevPosts];
                    });
                }
            };

            // Handle new subscriber posts
            const handleNewPost = (data: any) => {
                console.log('Received new subscriber post:', data);
                // For subscriber posts, we'll just refresh the feed to get the latest posts
                if (currentView.current === 'home') {
                    fetchPosts();
                }
            };

            socketService.onNewPublicPost(handleNewPublicPost);
            socketService.onNewPost(handleNewPost);
        }

        return () => {
            window.removeEventListener('openCreatePost', handleOpenCreatePost);

            // Clean up socket listeners but don't disconnect 
            // (keep connection for notifications)
            socketService.offNewPublicPost();
            socketService.offNewPost();
        };
    }, [user?.id, activeView]);

    // Update currentView ref when activeView changes
    useEffect(() => {
        currentView.current = activeView;
    }, [activeView]);

    // Cleanup socket connection on unmount
    useEffect(() => {
        return () => {
            if (socketConnected.current) {
                socketService.disconnect();
                socketConnected.current = false;
            }
        };
    }, []);

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    const handleProfileClick = (userId: string) => {
        navigate(`/profile/${userId}`);
    };

    const renderPost = (post: Post) => (
        <div key={post.id} className="p-2 p-sm-3 border-bottom">
            <div className="d-flex align-items-center mb-3">
                <div
                    className="position-relative me-2 me-sm-3"
                    onClick={() => handleProfileClick(post.user.id)}
                    style={{ cursor: 'pointer' }}
                >
                    <Avatar
                        src={post.user.avatar}
                        alt={post.user.username}
                        size="md"
                    />
                    <div className="position-absolute" style={{ top: '-2px', right: '-2px' }}>
                        <i className="feather-check-circle text-success font-xsss bg-white rounded-circle"></i>
                    </div>
                </div>
                <div className="flex-grow-1">
                    <h4
                        className="fw-700 text-grey-900 font-xsss mb-0"
                        onClick={() => handleProfileClick(post.user.id)}
                        style={{ cursor: 'pointer' }}
                    >
                        {post.user.username}
                        <i className="feather-check-circle text-success font-xsss ms-1"></i>
                    </h4>
                    <p className="fw-500 text-grey-500 font-xsss mb-0">{formatTimeAgo(post.createdAt)}</p>
                </div>
                <div className="ms-auto">
                    <i className="feather-more-horizontal text-grey-500 font-md cursor-pointer"></i>
                </div>
            </div>

            <div className="d-flex align-items-center mb-2">
                <h5 className="fw-700 text-grey-900 font-sm mb-0 me-2">{post.title}</h5>
                {!post.isPublic && (
                    <i className="feather-lock font-xss text-grey-500"></i>
                )}
            </div>
            {post.content && (
                <p className="fw-500 text-grey-500 lh-26 font-xsss mb-3">
                    {post.content}
                </p>
            )}

            {/* Media Grid */}
            {post.mediaUrls && post.mediaUrls.length > 0 && (
                <div className="mb-3">
                    {post.mediaUrls.length === 1 ? (
                        <div className="position-relative mb-3">
                            {post.mediaTypes[0]?.startsWith('image/') ? (
                                <MediaImage
                                    src={post.mediaUrls[0]}
                                    alt="Post media"
                                    className="w-100 rounded-3"
                                    style={{ height: 'auto', maxHeight: '400px', objectFit: 'cover' }}
                                />
                            ) : (
                                <video
                                    src={post.mediaUrls[0]}
                                    className="w-100 rounded-3"
                                    style={{ height: 'auto', maxHeight: '400px', objectFit: 'cover' }}
                                    controls
                                />
                            )}
                        </div>
                    ) : (
                        <div className="row g-2 mb-3">
                            {post.mediaUrls.slice(0, 4).map((url, index) => (
                                <div key={index} className={`col-${post.mediaUrls.length === 2 ? '6' : '6'}`}>
                                    <div className="position-relative">
                                        {post.mediaTypes[index]?.startsWith('image/') ? (
                                            <MediaImage
                                                src={url}
                                                alt={`Post media ${index + 1}`}
                                                className="w-100 rounded-3"
                                                style={{ height: '120px', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <video
                                                src={url}
                                                className="w-100 rounded-3"
                                                style={{ height: '120px', objectFit: 'cover' }}
                                                controls
                                            />
                                        )}
                                        {index === 3 && post.mediaUrls.length > 4 && (
                                            <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 rounded-3 d-flex align-items-center justify-content-center">
                                                <span className="text-white fw-700 font-sm">+{post.mediaUrls.length - 4}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Post Interactions */}
            <PostInteractions
                postId={post.id}
                initialLikesCount={post.likesCount}
                initialCommentsCount={post.commentsCount}
                onLikeUpdate={(newCount) => {
                    setPosts(prevPosts =>
                        prevPosts.map(p =>
                            p.id === post.id ? { ...p, likesCount: newCount } : p
                        )
                    );
                }}
                onCommentUpdate={(newCount) => {
                    setPosts(prevPosts =>
                        prevPosts.map(p =>
                            p.id === post.id ? { ...p, commentsCount: newCount } : p
                        )
                    );
                }}
            />
        </div>
    );

    const renderMainContent = () => {
        switch (activeView) {
            case 'profile':
                return <MyProfile />;
            case 'notifications':
                return <NotificationsPage />;
            case 'messages':
                return <MessagesPage />;
            case 'collections':
                return <CollectionsPage />;
            case 'subscriptions':
                return <SubscriptionsPage />;
            case 'settings':
                return <Settings />;
            case 'analytics':
                return <Analytics />;
            case 'creator-application':
                return <CreatorApplication />;
            case 'admin-dashboard':
                return <AdminDashboard />;
            case 'kyc-admin':
                return <KYCAdminPanel />;
            case 'home':
            default:
                return (
                    <>
                        {/* Header */}
                        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                            <div className="d-flex align-items-center">
                                {/* <button className="btn btn-link p-0 me-3" type="button" data-bs-toggle="offcanvas" data-bs-target="#drawerOffcanvas">
                                    <i className="feather-menu font-lg text-grey-700"></i>
                                </button> */}
                                <h4 className="fw-700 font-md mb-0">HOME</h4>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="d-none d-sm-block" style={{ width: '280px' }}>
                                    <SearchBar placeholder="Search by username or email..." />
                                </div>
                                <button className="btn btn-link p-0 d-sm-none" type="button">
                                    <i className="feather-search font-lg text-grey-700"></i>
                                </button>
                            </div>
                        </div>

                        {/* Compose Post */}
                        <div className="p-3 border-bottom">
                            <div
                                className="d-flex align-items-center p-3 bg-light rounded-3 cursor-pointer"
                                onClick={handleOpenCreatePost}
                            >
                                <i className="feather-edit font-md text-grey-500 me-3"></i>
                                <p className="text-grey-500 mb-0 font-xsss">What's on your mind?</p>
                            </div>
                        </div>

                        {/* Debug Tool */}
                        {/* <div className="p-3 border-bottom">
                            <ImageDebugger />
                        </div> */}


                        {/* Posts Content */}
                        <div className="posts-container">
                            {loading ? (
                                <div className="d-flex justify-content-center align-items-center p-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="alert alert-danger m-3" role="alert">
                                    <i className="feather-alert-circle me-2"></i>
                                    {error}
                                    <button
                                        className="btn btn-outline-primary btn-sm ms-2"
                                        onClick={fetchPosts}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : posts.length === 0 ? (
                                <div className="text-center p-5">
                                    <i className="feather-file-text display-4 text-grey-500 mb-3"></i>
                                    <h5 className="text-grey-500 fw-600">No posts yet</h5>
                                    <p className="text-grey-500 font-xsss">Be the first to share something!</p>
                                </div>
                            ) : (
                                posts.map(post => renderPost(post))
                            )}
                        </div>
                    </>
                );
        }
    };

    return (
        <Fragment>
            <div className="container-fluid vh-100 d-flex flex-column flex-lg-row">
                {/* Left Sidebar */}
                <div className="col-12 col-lg-3 col-xl-2 bg-white p-0 d-none d-lg-block" style={{ minWidth: '240px', maxWidth: '260px' }}>
                    <div className="p-3">
                        <h4 className="fw-700 font-sm mb-3">MENU</h4>

                        {/* Navigation Menu */}
                        <div className="nav-menu">
                            <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'home' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('home')}>
                                <i className="feather-home font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">Home</span>
                            </div>
                            {user?.role === 'creator' && (
                                <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'notifications' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('notifications')}>
                                    <i className="feather-bell font-lg me-3 text-grey-700"></i>
                                    <span className="font-sm fw-600 text-grey-900">Notifications</span>
                                </div>
                            )}
                            {/* <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'messages' ? 'bg-light rounded-3' : ''}`} onClick={() => setActiveView('messages')}>
                                <i className="feather-message-circle font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">Messages</span>
                            </div> */}
                            <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'subscriptions' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('subscriptions')}>
                                <i className="feather-credit-card font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">Subscriptions</span>
                            </div>
                            {(user?.role === 'consumer' || (user?.role === 'creator' && user?.kycStatus !== 'approved')) && (
                                <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'creator-application' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('creator-application')}>
                                    <i className="feather-plus-circle font-lg me-3 text-grey-700"></i>
                                    <span className="font-sm fw-600 text-grey-900">Creator Application</span>
                                </div>
                            )}

                            <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'profile' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('profile')}>
                                <i className="feather-user font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">My profile</span>
                            </div>
                            <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'analytics' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('analytics')}>
                                <i className="feather-bar-chart font-lg me-3 text-grey-700"></i>
                                <span className="font-sm fw-600 text-grey-900">Analytics</span>
                            </div>
                            {user?.role === 'admin' && (
                                <div className={`nav-item d-flex align-items-center py-3 cursor-pointer ${activeView === 'admin-dashboard' ? 'bg-light rounded-3' : ''}`} onClick={() => handleViewChange('admin-dashboard')}>
                                    <i className="feather-settings font-lg me-3 text-grey-700"></i>
                                    <span className="font-sm fw-600 text-grey-900">Admin Panel</span>
                                </div>
                            )}
                            {/* Logout Button */}
                            <div className="nav-item d-flex align-items-center py-3 cursor-pointer" onClick={handleLogout}>
                                <i className="feather-log-out font-lg me-3 text-danger"></i>
                                <span className="font-sm fw-600 text-danger">Logout</span>
                            </div>
                        </div>

                        {/* New Post Button */}
                        <div className="mt-3">
                            <button
                                className="btn btn-primary w-100 rounded-pill py-2 font-xs fw-700"
                                onClick={handleOpenCreatePost}
                            >
                                NEW POST
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="col-12 col-lg-6 col-xl-8 bg-white border-start-lg border-end-lg flex-grow-1" style={{ height: '100vh', overflowY: 'auto', paddingBottom: '80px' }}>
                    {renderMainContent()}
                </div>

            </div>

            {/* Drawer Menu */}
            <DrawerMenu onCreatePost={handleOpenCreatePost} onViewChange={handleViewChange} />

            {/* Create Post Modal */}
            <CreatePostModal
                isOpen={isCreatePostModalOpen}
                onClose={handleCloseCreatePost}
                onPostCreated={handlePostCreated}
            />

            {/* Bottom Navigation for Mobile */}
            <BottomNavigation
                activeView={activeView}
                onViewChange={handleViewChange}
                onCreatePost={handleOpenCreatePost}
            />
        </Fragment>
    );
};

export default Home;
